import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * The floating free shipping nudge pill: tells a US shopper how much more to
 * add for free shipping, opens the cart drawer on tap, and minimizes to a
 * small tab (choice persisted in localStorage). At or above the threshold it
 * only shows a brief "Free shipping unlocked" confirmation after a crossing,
 * never on initial page load -- the cart drawer owns the ladder from there.
 */
class AvShippingNudge extends HTMLElement {
  connectedCallback() {
    this.#thresholdCents = Number(this.dataset.thresholdCents ?? 0);
    this.#currency = this.dataset.currency || 'USD';
    this.#moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.#lastCents = 0;

    this.#body = this.querySelector('[data-nudge-open]');
    this.#toggle = this.querySelector('[data-nudge-toggle]');
    this.#tab = this.querySelector('[data-nudge-expand]');
    this.#text = this.querySelector('[data-nudge-text]');
    this.#fill = this.querySelector('[data-nudge-fill]');
    this.#short = this.querySelector('[data-nudge-short]');

    this.#body?.addEventListener('click', this.#onOpen);
    this.#toggle?.addEventListener('click', this.#onMinimize);
    this.#tab?.addEventListener('click', this.#onExpand);

    this.#setMinimized(this.#readMinimized(), { focus: false });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCart);
    this.#render(Number(this.dataset.initialTotalCents ?? 0), { initial: true });
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCart);
    clearTimeout(this.#hideTimer);
  }

  #thresholdCents = 0;
  #currency = 'USD';
  #moneyFormat = '${{amount}}';
  #lastCents = 0;
  #hideTimer = null;
  #body = null;
  #toggle = null;
  #tab = null;
  #text = null;
  #fill = null;
  #short = null;

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  // Product-page adds resolve the event with only { detail: { itemCount } },
  // no cart, so the first version never updated live. Read the subtotal from
  // /cart.js once the update settles (or after 2.5s if it never does).
  #onCart = (event) => {
    let done = false;
    const refresh = () => {
      if (done) return;
      done = true;
      fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
        .then((response) => response.json())
        .then((cart) => this.#render(Number(cart.items_subtotal_price ?? 0)))
        .catch(() => {});
    };
    event.promise?.then(refresh, refresh);
    setTimeout(refresh, 2500);
  };

  #onOpen = () => {
    document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
  };

  #onMinimize = () => {
    this.#setMinimized(true);
  };

  #onExpand = () => {
    this.#setMinimized(false);
  };

  #render(cents, { initial = false } = {}) {
    clearTimeout(this.#hideTimer);
    this.#hideTimer = null;

    const remaining = this.#thresholdCents - cents;
    const money = (value) => formatMoney(value, this.#moneyFormat, this.#currency);

    if (cents > 0 && remaining > 0) {
      // Short enough to fit the 220px pill on a 390px phone without an ellipsis.
      this.#text.textContent = `${money(remaining)} to free shipping`;
      this.#short.textContent = money(remaining);
      this.#fill.style.inlineSize = `${Math.min(100, (cents / this.#thresholdCents) * 100)}%`;
      this.hidden = false;
    } else if (!initial && this.#lastCents > 0 && this.#lastCents < this.#thresholdCents && cents >= this.#thresholdCents) {
      this.#text.textContent = 'Free shipping unlocked';
      this.#short.textContent = 'Free shipping';
      this.#fill.style.inlineSize = '100%';
      this.hidden = false;
      this.#hideTimer = setTimeout(() => {
        this.hidden = true;
        this.#hideTimer = null;
      }, 4000);
    } else {
      this.hidden = true;
    }

    this.#lastCents = cents;
  }

  #setMinimized(minimized, { focus = true } = {}) {
    this.classList.toggle('av-shipping-nudge--min', minimized);
    if (this.#tab) this.#tab.hidden = !minimized;
    this.#toggle?.setAttribute('aria-expanded', String(!minimized));
    try {
      if (minimized) localStorage.setItem('av-shipping-nudge-min', '1');
      else localStorage.removeItem('av-shipping-nudge-min');
    } catch {
      // Storage can throw (private mode, quota); the choice just won't persist.
    }
    if (focus) (minimized ? this.#tab : this.#toggle)?.focus();
  }

  #readMinimized() {
    try {
      return localStorage.getItem('av-shipping-nudge-min') === '1';
    } catch {
      return false;
    }
  }
}

if (!customElements.get('av-shipping-nudge')) {
  customElements.define('av-shipping-nudge', AvShippingNudge);
}
